'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Heading, Button, Picture } from '../../components/index.js'
import { withLayout } from '../../shared/Styles.js'
import type { Media } from '../../types/props.js'
import { getLocalizedPath } from '../../helpers/index.js'
import { useAiuiContext } from '../AiuiProvider/index.js'

export interface PromotionalBarSemanticName {
  root: 'root'
  content: 'content'
  title: 'title'
  description: 'description'
  button: 'button'
}

/**
 * 响应式背景图片配置
 */
export interface ResponsiveBackgroundImage {
  /** 移动端图片 (<768px) */
  mobile?: Media
  /** 平板图片 (768-1440px) */
  tablet?: Media
  /** 笔记本图片 (1025-1440px) */
  laptop?: Media
  /** 桌面图片 (1441-1920px) */
  desktop?: Media
  /** 超大桌面图片 (≥1921px) */
  lgDesktop?: Media
}

/**
 * PromotionalBar 业务组件数据接口
 */
export interface PromotionalBarData {
  /** 组件内容标题 */
  contentTitle?: string
  /** 组件内容描述 */
  contentDesc?: string
  /** 按钮文本 */
  buttonText?: string
  /** 按钮链接 */
  buttonLink?: string
  /** 背景图片 - 响应式图片对象 */
  backgroundImage?: ResponsiveBackgroundImage
}

export interface PromotionalBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 业务数据 */
  data: PromotionalBarData
  classNames?: Partial<Record<keyof PromotionalBarSemanticName, string>>
}

/**
 * 将响应式背景图片对象转换为 Picture 组件所需的字符串格式
 */
const convertResponsiveImageToString = (backgroundImage: ResponsiveBackgroundImage | undefined): string | undefined => {
  if (!backgroundImage) return undefined

  const { mobile, tablet, laptop, desktop, lgDesktop } = backgroundImage
  const parts: string[] = []

  // 辅助函数：从 Media 对象中提取 URL
  const getUrl = (media: Media | undefined): string | undefined => {
    return media?.url
  }

  // 默认图片（最大尺寸）放在最前面，不需要断点
  const lgDesktopUrl = getUrl(lgDesktop)
  const desktopUrl = getUrl(desktop)
  const laptopUrl = getUrl(laptop)
  const tabletUrl = getUrl(tablet)
  const mobileUrl = getUrl(mobile)

  if (lgDesktopUrl) parts.push(lgDesktopUrl)
  else if (desktopUrl) parts.push(desktopUrl)
  else if (laptopUrl) parts.push(laptopUrl)
  else if (tabletUrl) parts.push(tabletUrl)
  else if (mobileUrl) parts.push(mobileUrl)

  // 按断点从大到小添加
  if (desktopUrl && lgDesktopUrl) parts.push(`${desktopUrl} 1920`)
  if (laptopUrl && (desktopUrl || lgDesktopUrl)) parts.push(`${laptopUrl} 1440`)
  if (tabletUrl && (laptopUrl || desktopUrl || lgDesktopUrl)) parts.push(`${tabletUrl} 1024`)
  if (mobileUrl && (tabletUrl || laptopUrl || desktopUrl || lgDesktopUrl)) parts.push(`${mobileUrl} 768`)

  return parts.length > 0 ? parts.join(', ') : undefined
}

/**
 * PromotionalBar - 推广栏
 *
 * @description 推广栏
 */
const PromotionalBar = React.forwardRef<HTMLDivElement, PromotionalBarProps>(
  ({ classNames, data, className, ...rest }, ref) => {
    const { locale = 'us' } = useAiuiContext()
    const { backgroundImage } = data
    const pictureSource = React.useMemo(() => {
      return convertResponsiveImageToString(backgroundImage)
    }, [backgroundImage])
    return (
      <div
        ref={ref}
        {...rest}
        className={cn('laptop:h-[192px] lg-desktop:h-[240px] h-[240px]', classNames?.root, className)}
      >
        {/* 推广栏内容区域 - 根据具体业务需求定制 */}
        <div className="promotional-bar-content rounded-card relative h-full overflow-hidden">
          <div
            className={cn(
              'laptop:px-8 laptop:justify-center lg-desktop:max-w-[620px] laptop:max-w-[392px] desktop:max-w-[584px] laptop:py-0 tablet:px-8 laptop:pr-0 relative z-20 flex h-full flex-col p-4',
              classNames?.content
            )}
          >
            {data.contentTitle && (
              <Heading
                className={cn('line-clamp-2 text-[#F5F6F7]', classNames?.title)}
                html={data.contentTitle}
                size={3}
              />
            )}
            {data.contentDesc && (
              <p
                className={cn(
                  'laptop:text-[16px] laptop:mt-2 lg-desktop:text-[18px] mt-1 line-clamp-2 text-[14px] font-bold leading-[1.4] text-[#F5F6F7]',
                  classNames?.description
                )}
                dangerouslySetInnerHTML={{ __html: data.contentDesc }}
              />
            )}
            <div className="laptop:mt-4 mt-2">
              {data.buttonText && (
                <Button
                  as="a"
                  variant="link"
                  className={cn('!p-0 text-[#F5F6F7]', classNames?.button)}
                  href={getLocalizedPath(data.buttonLink || '', locale)}
                  iconClassName="size-4"
                >
                  {data.buttonText}
                </Button>
              )}
            </div>
          </div>
          <Picture
            source={pictureSource}
            className="absolute inset-0 z-10 "
            imgClassName="h-full object-cover transition-transform duration-300 ease-in-out hover:scale-[1.05]"
          />
        </div>
      </div>
    )
  }
)

PromotionalBar.displayName = 'PromotionalBar'
export default withLayout(PromotionalBar)
